import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import type { GameStateView } from "@shared/types";
import { LogoMark, ShowcaseCredit } from "../components/Brand";
import { Icon } from "../components/Icon";
import { LanguagePicker } from "../components/Pickers";
import { QuestionImage } from "../components/QuestionImage";
import { RoomFrame } from "../components/RoomFrame";
import { Standings } from "../components/Standings";
import { RoomCode } from "../components/ui";
import { useFx } from "../lib/fx";
import { useT } from "../lib/i18n";
import { useRoom } from "../lib/room";

// ─────────────────────────────────────────────────────────────────────────────
//  The Showcase: the big shared screen (TV / projector).
//
//  Strictly read-only — it never shows an answer before the host reveals it and
//  has no controls, so it is safe to point a room full of people at. This is
//  also the screen people stare at for an hour, so it carries the sign-off.
// ─────────────────────────────────────────────────────────────────────────────

export default function Show() {
  const { code = "" } = useParams<{ code: string }>();
  const conn = useRoom(code, "show");
  const toast = useFx();
  const t = useT();

  return (
    <RoomFrame state={conn.state} status={conn.status} fatal={conn.fatal}>
      {conn.state && (
        <div className="min-h-full flex flex-col relative">
          {/* The header is chrome, not content: quiet, and the one control on
              it stays invisible until someone actually reaches for it. */}
          <header className="group flex items-center gap-4 px-6 py-4 shrink-0">
            <LogoMark size={26} className="opacity-80" />
            <h1 className="type-display text-2xl text-accentLight truncate">
              {conn.state.settings.title || t("appName")}
            </h1>
            <span className="text-ink/40 text-lg truncate">{conn.state.roundName}</span>
            <div className="flex-1" />
            <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
              <LanguagePicker compact />
            </div>
            <RoomCode code={code} size="lg" className="text-ink/45" />
          </header>

          {conn.state.phase === "board" ? (
            <BoardShow game={conn.state} code={code} />
          ) : conn.state.phase === "question" ? (
            <QuestionShow game={conn.state} code={code} />
          ) : conn.state.phase === "wager" ? (
            <WagerShow game={conn.state} />
          ) : (
            <Standings game={conn.state} />
          )}

          <ShowcaseCredit />

          {toast && (
            <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 inline-flex items-center gap-3 px-7 py-4 rounded-full bg-successStrong text-white font-bold text-2xl shadow-pop anim-pop">
              <Icon name="party" size="1.5rem" />
              {toast.name} +{toast.points}
            </div>
          )}
        </div>
      )}
    </RoomFrame>
  );
}

/**
 * Scores along the bottom. With nobody in the game yet this becomes the join
 * prompt, so an empty room on a projector still tells people what to do.
 */
function ScoresStrip({ game, code }: { game: GameStateView; code: string }) {
  const t = useT();
  const joinUrl = `${window.location.origin}/r/${code}/play`;

  if (game.players.length === 0) {
    return (
      <div className="shrink-0 px-6 py-6 border-t border-line flex items-center justify-center gap-7">
        <div className="bg-white p-3 rounded-card shadow-pop shrink-0">
          <QRCodeSVG value={joinUrl} size={116} includeMargin={false} />
        </div>
        <div className="text-center sm:text-left min-w-0">
          <p className="text-lg sm:text-2xl text-ink/60">{t("scanToPlay")}</p>
          <RoomCode code={code} size="xl" className="text-accentLight" />
        </div>
      </div>
    );
  }

  return (
    <div className="shrink-0 px-6 py-4 flex gap-2.5 overflow-x-auto border-t border-line">
      {game.players.map((p, i) => (
        <div
          key={p.id}
          className={`flex items-center gap-2.5 pl-2.5 pr-4 py-2 rounded-card shrink-0 ${
            i === 0 && p.score > 0 ? "bg-accent text-onAccent" : "bg-ink/[0.08]"
          }`}
        >
          <img src={p.avatarUrl} alt="" className="w-9 h-9 rounded-control bg-white" />
          <span className="font-semibold">{p.name}</span>
          <span className="font-bold tabular text-xl">{p.score}</span>
        </div>
      ))}
    </div>
  );
}

function BoardShow({ game, code }: { game: GameStateView; code: string }) {
  const cols = game.categories.length || 1;
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 px-6 pb-2 min-h-0">
        <div
          className="grid gap-2.5 h-full"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {game.categories.map((cat) => (
            <div key={cat.id} className="flex flex-col gap-2.5 min-h-0">
              <div className="bg-boardDark rounded-card px-3 py-4 text-center type-display text-lg uppercase min-h-[72px] flex items-center justify-center border-b-2 border-accent/40">
                {cat.name}
              </div>
              {cat.cells.map((cell) => (
                <div
                  key={cell.points}
                  className={`flex-1 rounded-card type-display text-4xl sm:text-6xl flex items-center justify-center tabular transition-colors ${
                    cell.used ? "bg-board/15" : "bg-board text-onBoard shadow-tile"
                  }`}
                >
                  {cell.used ? "" : cell.points}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <ScoresStrip game={game} code={code} />
    </div>
  );
}

function QuestionShow({ game, code }: { game: GameStateView; code: string }) {
  const t = useT();
  const q = game.current;
  const first = game.buzzes[0];
  if (!q) return null;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 px-6 pb-4 flex flex-col items-center justify-center gap-6 min-h-0">
        <div className="type-eyebrow text-accent text-sm">
          {q.categoryName} · {q.points}
        </div>

        {/*
          Sized to its content with a ceiling, not stretched to fill: a short
          question in a screen-height box reads as a rendering bug.
        */}
        <div className="bg-board rounded-surface px-12 py-14 w-full max-w-5xl flex flex-col items-center justify-center gap-6 text-center shadow-pop min-h-[34vh] max-h-[64vh] overflow-y-auto">
          <QuestionImage src={q.image} className="max-h-[34vh] w-auto rounded-card object-contain" />
          <p className="type-display text-5xl lg:text-7xl leading-[1.05]">{q.question}</p>
          {q.answerRevealed && q.answer && (
            <p className="text-3xl sm:text-4xl text-accentLight font-semibold border-t border-ink/15 pt-5 w-full">
              {q.answer}
            </p>
          )}
        </div>

        <div className="h-14 flex items-center">
          {first ? (
            <div className="flex items-center gap-3 anim-pop">
              <img src={first.avatarUrl} alt="" className="w-12 h-12 rounded-card bg-white" />
              <span className="text-ink/45 text-xl">{t("onTheClock")}</span>
              <span className="type-display text-3xl text-accentLight">{first.name}</span>
            </div>
          ) : game.buzzersOpen ? (
            <span className="text-success text-2xl inline-flex items-center gap-3 font-semibold">
              <span className="w-3 h-3 rounded-full bg-success animate-pulse" />
              {t("buzzersOpenBig")}
            </span>
          ) : (
            <span className="text-ink/35 text-xl">{t("aboutToStart")}</span>
          )}
        </div>
      </div>
      <ScoresStrip game={game} code={code} />
    </div>
  );
}

function WagerShow({ game }: { game: GameStateView }) {
  const t = useT();
  const w = game.wager;
  if (!w) return null;

  if (w.stage === "reveal") {
    return (
      <div className="flex-1 px-6 pb-6 flex flex-col items-center gap-7 overflow-y-auto">
        <div className="text-center">
          <div className="type-eyebrow text-accent">
            {w.name} · {t("reveal")}
          </div>
          <p className="type-display text-3xl sm:text-5xl mt-2">{w.question}</p>
        </div>
        <div className="w-full max-w-6xl grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {w.entries
            .filter((e) => e.wagered || e.answered)
            .map((e) => (
              <div
                key={e.playerId}
                className={`rounded-card p-5 text-center anim-pop ${
                  e.judged === "correct"
                    ? "bg-successStrong text-white"
                    : e.judged === "wrong"
                      ? "bg-dangerStrong text-white"
                      : "bg-ink/[0.08]"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <img src={e.avatarUrl} alt="" className="w-9 h-9 rounded-control bg-white" />
                  <span className="font-semibold text-lg">{e.name}</span>
                </div>
                <div className="type-display text-3xl mt-3 break-words">{e.answer || "—"}</div>
                <div className="mt-2 text-sm opacity-75 tabular">
                  {t("wagerLabel")} {e.wager}
                  {e.judged === "correct"
                    ? ` · +${e.wager}`
                    : e.judged === "wrong"
                      ? ` · −${e.wager}`
                      : ""}
                </div>
              </div>
            ))}
        </div>
      </div>
    );
  }

  const waitingLabel = w.stage === "betting" ? t("placeYourWagers") : t("writeYourAnswers");
  return (
    <div className="flex-1 px-6 pb-6 flex flex-col items-center justify-center gap-9 overflow-y-auto">
      <div className="text-center">
        <div className="type-eyebrow text-accent">{w.name}</div>
        <h2 className="type-display text-4xl sm:text-6xl text-accentLight mt-2">{waitingLabel}</h2>
      </div>
      {w.stage === "question" && (
        <div className="bg-board rounded-surface px-10 py-12 w-full max-w-4xl text-center shadow-pop flex flex-col items-center gap-6">
          <QuestionImage src={w.image} className="max-h-[34vh] w-auto rounded-card object-contain" />
          <p className="type-display text-5xl lg:text-7xl leading-[1.05]">{w.question}</p>
        </div>
      )}
      <div className="w-full max-w-3xl flex flex-wrap justify-center gap-2.5">
        {w.entries.map((e) => {
          const done = w.stage === "betting" ? e.wagered : e.answered;
          return (
            <div
              key={e.playerId}
              className={`flex items-center gap-2.5 pl-2.5 pr-4 py-2.5 rounded-card transition-colors ${
                done ? "bg-successStrong text-white" : "bg-ink/[0.08] text-ink/70"
              }`}
            >
              <img src={e.avatarUrl} alt="" className="w-9 h-9 rounded-control bg-white" />
              <span className="font-semibold text-lg">{e.name}</span>
              {done ? <Icon name="check" /> : <span className="opacity-50">…</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
